/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { EMPTY, zip, Observable } from 'rxjs';
import { filter, takeUntil, map, delay, withLatestFrom, tap } from 'rxjs/operators';
import { alternativeBufferTime } from 'src/app/plain-javascript/services/rxjs-internal-extensions';

import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
import { TicketActionTypes, FancyTicketAction } from 'src/app/app-data/actions/ticket-actions';
import { PlanState } from 'src/app/services/plan-state.service';

import { UndoItem } from './UndoItem';
import { UndoTypes, UndoTitles } from './UndoTypes';
import { BaseUndoOperation } from './base-undo-operation';

import { Ticket } from '@touchplan/common';

import {
	serializeTicket,
	metaizeTicket,
	getTimelineIdForTicket,
	monitorExternalDeletion,
} from './undo-utils';
import { TicketMeta } from 'src/app/app-data/models/TicketMeta';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../logging/LoggerServiceFactory';
import { TicketEditCommonDesktopService } from '../docked-ticket/services/ticket-edit-common-desktop.service';

interface IEditInvalidationCache {
	ticketIds: { [key: string]: string };
}

interface IEditUndoItem extends UndoItem {
	data: [TicketMeta[], TicketMeta[]];
	invalidationCache: IEditInvalidationCache;
}

export class TicketEditOperation extends BaseUndoOperation<IEditInvalidationCache, IEditUndoItem> {
	public readonly undoType = UndoTypes.ticketEdit;
	public title = UndoTitles.ticketEdit;

	private logger: ILoggerService;

	constructor(
		private ticketList: TicketList,
		private planState: PlanState,
		private ticketEditCommonService: TicketEditCommonDesktopService,
		private previousDeletion$: Observable<FancyTicketAction[]>
	) {
		super();
		this.logger = LoggerServiceFactory.create('TicketEditOperation');

		// tuples
		let ticketEditData: [TicketMeta[], TicketMeta[]] = [[], []];

		this.ticketList.rawEvent$
			.pipe(
				filter(
					(event) =>
						event.type === TicketActionTypes.ticketChangedToEdit ||
						event.type === TicketActionTypes.ticketChangedToLocationsEdit
				),
				map((event) => this.decorateTicketData(event.payload)),
				alternativeBufferTime(20),
				// wee, arbitrary delay
				// This exists because there's a case where the end event for one edit and the start event for the next edit fire basically
				// simultaneously (switching edited tickets, and easier to replicate: multi-select-menu cycle ticket). Essentially there's
				// two ways to fix that. Remove state from the service itself and carry it through in an observable stream. And add an arbitrary
				// delay to the start event. Since this is already written to be "stateful", just stick with the arbitrary delay for now. See
				// the end of the file for a stateless strategy.
				delay(300),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((payload: TicketMeta[]) => {
				ticketEditData[0] = payload;
			});

		const ticketChangedToDefault: Observable<TicketMeta[]> = ticketList.rawEvent$.pipe(
			filter(
				(event) => event.type === TicketActionTypes.ticketChangedToDefault && ticketEditData[0].length > 0
			),
			map((event) => this.decorateTicketData(event.payload)),
			alternativeBufferTime(20),
			takeUntil(this.shutdownSubject$)
		);

		//debug version
		// zip(
		// 	ticketChangedToDefault.pipe(tap( s => this.logger.log('!!! changed to default', s))),
		// 	ticketEditService.markAsDirtyAndSave$.pipe(tap( s => this.logger.log('!!! mark as dirty and save', s)))
		// ).pipe(
		zip(ticketChangedToDefault, this.ticketEditCommonService.ticketEditClosedWithChanges())
			.pipe(
				// reason for withLatestFrom is that there are cases where ticketChangedToDefault doesn't emit, e.g. ticket delete,
				// this causes zip to store stale closeEditAndCheckDirty$ data
				withLatestFrom(this.ticketEditCommonService.ticketEditClosedWithChanges()),
				filter((combined) => combined[0][0].length > 0 && ticketEditData[0].length === combined[0][0].length),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((combined) => {
				if (combined[1]) {
					ticketEditData[1] = combined[0][0];
					this.title = ticketEditData[0].length > 1 ? UndoTitles.multiTicketEdit : UndoTitles.ticketEdit;
					this.onPush(this.makeUndoItem(ticketEditData));
				}

				ticketEditData = [[], []];
			});

		//monitor ticket removal for invalidation
		//TODO - enhancement, after filtering, group by ticket id, audit for a bit to try and only emit once per ticket id
		// so as to better handle that fact that there are multiple deletion events, usually called in sequence
		ticketList.rawEvent$
			.pipe(monitorExternalDeletion(this.previousDeletion$), takeUntil(this.shutdownSubject$))
			.subscribe((action) => {
				this.checkDeleteInvalidation(action.key);
			});

		// lock/unlock based on number tickets being edited
		ticketList.editTicket$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((tickets: Map<string, Ticket>) => this.onLock(!!tickets.size));
	}

	private decorateTicketData(ticket: Ticket): TicketMeta {
		const timelineId = getTimelineIdForTicket(ticket, this.planState.timelineTickets, this.planState.planId);
		const customFieldTicketValue = this.planState.customFieldsApi.cloneCustomFieldTicketValues(ticket.$id);
		return metaizeTicket(serializeTicket(ticket), timelineId, customFieldTicketValue);
	}

	private makeUndoItem(data: [TicketMeta[], TicketMeta[]]): IEditUndoItem {
		return {
			title: this.title,
			data,
			undoType: this.undoType,
			invalidationCache: this.makeInvalidationCache(data),
		};
	}

	private makeInvalidationCache(data: [TicketMeta[], TicketMeta[]]): IEditInvalidationCache {
		const ids = {};
		data.forEach((list) => {
			list.forEach((a) => (ids[a.ticket.$id] = a.ticket.$id));
		});
		return {
			ticketIds: ids,
		};
	}

	private checkDeleteInvalidation(ticketId: string) {
		this.onVerifyInvalidation((cache: IEditInvalidationCache, list: IEditUndoItem[]) => {
			if (!cache.ticketIds[ticketId]) {
				return null;
			}

			return list.filter((item) => item.invalidationCache.ticketIds[ticketId]);
		});
	}

	private genericUndo(cacheData: TicketMeta[]) {
		if (cacheData && cacheData.length) {
			cacheData.forEach((ticketMeta) => {
				//update normal data
				this.planState.actions.updateTicketLiveData(
					ticketMeta.ticket.$id,
					ticketMeta.ticket.view,
					undefined,
					{ overwrite: true }
				);

				//update timeline data
				//this works assuming that you can only currently edit tickets on the plan you're on
				const samePlanId = this.planState.timelineTickets.makeCombinedId(
					ticketMeta.ticket.$id,
					this.planState.planId
				);
				//add it back
				if (ticketMeta.timelineIdx && !this.planState.timelineTickets.syncList.has(ticketMeta.timelineIdx)) {
					const split = this.planState.timelineTickets.splitId(ticketMeta.timelineIdx);
					this.planState.legacyActions.toggleTimelineTicket(split[0], split[1], true, true);
				}
				//remove it
				else if (!ticketMeta.timelineIdx && this.planState.timelineTickets.syncList.has(samePlanId)) {
					const split = this.planState.timelineTickets.splitId(samePlanId);
					this.planState.legacyActions.toggleTimelineTicket(split[0], split[1], false, true);
				}

				// undo data snapshots the before/after states, applying before/after snapshot doesn't remove the deleted entries
				// cleanup all data for current ticket and rebuild from undo data
				this.planState.customFieldsApi.removeCustomFieldTicketValue(ticketMeta.ticket.$id);
				this.planState.customFieldsApi.createCustomFieldTicketValues(
					ticketMeta.ticket.$id,
					ticketMeta.customFieldTicketValues
				);
			});

			this.planState.actions.persistTicketData(cacheData.map((meta) => meta.ticket));
		}

		return EMPTY;
	}

	public applyUndo(undoItem: IEditUndoItem) {
		return this.genericUndo(undoItem.data[0]);
	}

	public applyRedo(undoItem: IEditUndoItem) {
		return this.genericUndo(undoItem.data[1]);
	}

	public calculateCombinedInvalidation(list: IEditUndoItem[], existingCache: IEditInvalidationCache) {
		const ticketIds = {};

		list.forEach((item) => {
			for (const id in item.invalidationCache.ticketIds) {
				if (id) {
					ticketIds[id] = id;
				}
			}
		});

		return { ticketIds };
	}
}

// Potential rewrite if timing issue come up (make stateless)
// - modify the end stream to be a race between two streams
//   - the first is the existing zip between changedToDefault and the dirty check
//   - the second is just changedToDefault
// - map those streams so that they return the changed tickets and some information about which path they went down
// - zip the start stream and the end stream together instead of storing a tuple on the service. The zip will maintain ordered state,
//   so it effectively doesn't matter whether the start or the end emits first
// - add a filter at the end (post zip) that rejects anything that didn't come from the dirty path

// before doing that, there's likely to be some bad interaction with ticket drag
// - zip won't play nice because the changed to default will emit on drag end
// - combineSequence won't play nice because replaying historical events is kind of the point here
// - likely want to switchMap the end with the start emitting once...

//External data writeup
// when snapshotting an undo
// - need ticket data of all tickets being edited before and after saving
// - need ticket data of all dependency updated tickets before and after
// - need a way to differentiate between the two types of ticket data (property and same list, or separate lists?)
// - need timeline data for the edited tickets (or both if that's easier)
// - skip exception day data, leave note

// when applying an undo
// - update tickets from the previous undo's deleted list
// - update tickets from the previous undo's dependency edited list
// - update timeline ticket data to match what is stored in the ticketMeta
// - skip exception day data, leave note

// when applying a redo
// - update tickets from the post edit list
// - update tickets from the post edit dependency edited list
// - update timeline ticket data to match what is stored in the ticketMeta for both lists (maybe just edit list?)
// - skip exception day data, leave note

// when invalidating
// - when anything in either list is deleted
// - cache needs to maintain either list
